import { AuthBridgeMessage } from "./auth-bridge-contracts.interface";
import { PostMessage } from "./post-message.interface";
import { WhatsappChatMessage, ProfilePicThumbEvent, Chat } from "./whatsapp-types";
import { RawWaStateStatus } from "./message-service-interface";

/**
 * Known message-service commands and their payload/response contracts — the
 * cross-context (page <-> content <-> background) counterpart to the WhatsApp tools
 * `WaToolContractMap`. Single source of truth for every command the MessageService
 * event bus dispatches.
 *
 * Lives in bt-common (imported by extension + backend, no cross-layer imports). Payloads
 * whose concrete types live in the extension / wa-communication layers (the full chrome
 * message envelope, WhatsApp messages, group/webhook events, raw WA state) are typed
 * `unknown` here — never `any` — and the extension-side handlers narrow them.
 */
export const MESSAGE_COMMANDS = {
  // --- Background: tab / runtime / fetch ---
  OPEN_TAB: "openTab",
  LAUNCH_WEB_AUTH_FLOW: "launchWebAuthFlow",
  RELAY_TO_TAB: "relayToTab",
  GET_CURRENT_VERSION: "getCurrentVersion",
  FETCH_URL_CONTENT: "fetchUrlContent",
  FETCH_URL_CONTENT_AS_BLOB: "fetchUrlContentAsBlob",
  FETCH_ASSET_BLOB: "fetchAssetBlob",
  GET_PROFILE_PIC_FROM_WA_TAB: "getProfilePicFromWaTab",
  GET_USER_INFO: "getUserInfo",

  // --- Auth bridge (hub <-> surfaces) ---
  GET_TOKEN: "get-token",
  SET_TOKEN: "set-token",
  LOGOUT: "logout",
  WEB_LOGOUT: "web-logout",
  CLEAR_LOCAL_TOKEN: "clear-local-token",
  SET_LOCAL_TOKEN: "set-local-token",
  ACCESS_TOKEN: "accessToken",

  // --- Extension meta ---
  CURRENT_EXTENSION_VERSION: "currentExtensionVersion",
  TRANSCRIBE_PROGRESS: "transcribeProgress",
  USER_INFO: "userInfo",

  // --- WA connection state ---
  APP_STATE_CHANGED: "appStateChanged",
  WA_LOGOUT: "waLogout",
  QR_CHANGED: "qr-changed",
  WA_STATE_HEALTH_CHECK: "waStateHealthCheck",
  WAPI_INITIALIZED: "wapi-initialized",

  // --- WA messages / chat events ---
  NEW_MESSAGE_RECEIVED: "new_message_received",
  MESSAGE_ACK_CHANGED: "messageAckChanged",
  MESSAGE_REMOVED: "messageRemoved",
  MESSAGE_REVOKED: "messageRevoked",
  CIPHERTEXT_MESSAGE_ADDED: "ciphertextMessageAdded",
  RECEIVED_REACTION_ADDED: "received_reaction_added",
  UNREAD_COUNT_CHANGED: "unreadCountChanged",
  CURRENT_CHAT: "currentChat",
  ME: "me",
  PROFILE_PIC_THUMB: "profilePicThumb",
  INVALID_WA_PHONE_NUMBER: "invalidWAPhoneNumber",
  GET_POLL_DATA_RESPONSE: "getPollDataResponse",
  CHECK_IF_REPLYING_TO_MESSAGE_RESPONSE: "checkIfReplyingToMessageResponse",

  // --- WA webhooks (fire-and-forget, ack only) ---
  ACK_CHANGED_WEBHOOK: "ack_changed_webhook",
  GROUP_ADMIN_CHANGED_WEBHOOK: "group_admin_changed_webhook",
  GROUP_DESCRIPTION_CHANGED_WEBHOOK: "group_description_changed_webhook",
  GROUP_MESSAGE_PINNED_WEBHOOK: "group_message_pinned_webhook",
  GROUP_NAME_CHANGED_WEBHOOK: "group_name_changed_webhook",
  MESSAGE_DELETED_REVOKED_WEBHOOK: "message_deleted_revoked_webhook",
  MESSAGE_EDITED_WEBHOOK: "message_edited_webhook",
  PARTICIPANT_ADDED_BY_ADMIN_WEBHOOK: "participant_added_by_admin_webhook",
  PARTICIPANT_JOINED_VIA_LINK_WEBHOOK: "participant_joined_via_link_webhook",
  PARTICIPANT_KICKED_FROM_GROUP_WEBHOOK: "participant_kicked_from_group_webhook",
  PARTICIPANT_LEFT_GROUP_WEBHOOK: "participant_left_group_webhook",
  POLL_VOTE_WEBHOOK: "poll_vote_webhook",
  REPLY_TO_MY_MESSAGE_WEBHOOK: "reply_to_my_message_webhook",

  // --- Queries forwarded to the page world ---
  CHECK_IF_CHAT_HAS_NEWER_MESSAGES: "checkIfChatHasNewerMessagesNotFromMe",
  CHECK_IF_REPLYING_TO_MESSAGE: "checkIfReplyingToMessage",
  CLEAR_CHAT_INPUT: "clearChatInput",
  CLOSE_MEDIA_PREVIEW: "closeMediaPreview",
  FOCUS_CONTACT: "focusContact",
  GET_CHATS_AND_CONTACTS: "getChatsAndContacts",
  GET_MEDIA: "getMedia",
  GET_WA_MESSAGE_ACK_DATA: "getWAMessageAckData",
  GET_WA_CONTACT_INFO_FROM_LIST_ITEM_SELECTOR: "getWaContactInfoFromListItemSelector",
  GET_WA_ME_USER: "getWaMeUser",
  GET_WA_VERSION: "getWaVersion",
} as const;

export type MessageCommand = (typeof MESSAGE_COMMANDS)[keyof typeof MESSAGE_COMMANDS];

/** Ack-only response (handler calls `sendResponse({})` purely to settle the promise). */
export type Ack = Record<string, never>;
/** Ack-data returned by getWAMessageAckData. */
export interface WaMessageAck { messageId: string; delivery: number; read: number; played: number; }
/** Blob fetch result (fetchAssetBlob). */
export interface FetchAssetBlobResult { success: boolean; error?: string; data?: { dataBase64: string; mimeType: string; size: number }; }

/**
 * Result of `getUserInfo` — the `chrome.identity.UserInfo` shape, inlined so
 * bt-common stays dependency-free and type-checks in every consumer (the
 * backend build does not pull in `@types/chrome`). Mirrors the runtime value
 * the extension's `chrome.identity.getProfileUserInfo` returns.
 */
export interface ChromeIdentityUserInfo { id: string; email: string; }

/**
 * cmd -> { Args (payload the handler receives), Result (sendResponse shape; `void` when
 * the handler does not respond or only acks) }. Mirrors WaToolContractMap. Full chrome
 * message-envelope payloads are `unknown` here (the envelope type lives in the frontend
 * common layer); handlers narrow them.
 */
export interface MessageCommandContractMap {
  // Background
  [MESSAGE_COMMANDS.OPEN_TAB]: { Args: PostMessage<string>; Result: void };
  [MESSAGE_COMMANDS.LAUNCH_WEB_AUTH_FLOW]: { Args: PostMessage; Result: { accessToken: string } | { error: string } };
  [MESSAGE_COMMANDS.RELAY_TO_TAB]: { Args: { relayedCmd?: string; relayedData?: unknown }; Result: boolean };
  [MESSAGE_COMMANDS.GET_CURRENT_VERSION]: { Args: PostMessage; Result: void };
  [MESSAGE_COMMANDS.FETCH_URL_CONTENT]: { Args: string; Result: string };
  [MESSAGE_COMMANDS.FETCH_URL_CONTENT_AS_BLOB]: { Args: string; Result: string | { error: string } };
  [MESSAGE_COMMANDS.FETCH_ASSET_BLOB]: { Args: { url?: unknown }; Result: FetchAssetBlobResult };
  [MESSAGE_COMMANDS.GET_PROFILE_PIC_FROM_WA_TAB]: { Args: { contactIds: string[] }; Result: { success: boolean; error?: string } };
  [MESSAGE_COMMANDS.GET_USER_INFO]: { Args: PostMessage; Result: ChromeIdentityUserInfo };

  // Auth bridge
  [MESSAGE_COMMANDS.GET_TOKEN]: { Args: PostMessage; Result: { accessToken: string | null } };
  [MESSAGE_COMMANDS.SET_TOKEN]: { Args: AuthBridgeMessage; Result: void };
  [MESSAGE_COMMANDS.LOGOUT]: { Args: void; Result: void };
  [MESSAGE_COMMANDS.WEB_LOGOUT]: { Args: void; Result: void };
  [MESSAGE_COMMANDS.CLEAR_LOCAL_TOKEN]: { Args: void; Result: void };
  [MESSAGE_COMMANDS.SET_LOCAL_TOKEN]: { Args: AuthBridgeMessage; Result: void };
  [MESSAGE_COMMANDS.ACCESS_TOKEN]: { Args: string; Result: void };

  // Extension meta
  [MESSAGE_COMMANDS.CURRENT_EXTENSION_VERSION]: { Args: { currentVersion?: string }; Result: void };
  [MESSAGE_COMMANDS.TRANSCRIBE_PROGRESS]: { Args: unknown; Result: void };
  [MESSAGE_COMMANDS.USER_INFO]: { Args: PostMessage; Result: void };

  // WA connection state
  [MESSAGE_COMMANDS.APP_STATE_CHANGED]: { Args: RawWaStateStatus; Result: void };
  [MESSAGE_COMMANDS.WA_LOGOUT]: { Args: RawWaStateStatus; Result: void };
  [MESSAGE_COMMANDS.QR_CHANGED]: { Args: RawWaStateStatus; Result: void };
  [MESSAGE_COMMANDS.WA_STATE_HEALTH_CHECK]: { Args: RawWaStateStatus; Result: void };
  [MESSAGE_COMMANDS.WAPI_INITIALIZED]: { Args: PostMessage; Result: Ack };

  // WA messages / chat events
  [MESSAGE_COMMANDS.NEW_MESSAGE_RECEIVED]: { Args: WhatsappChatMessage; Result: Ack };
  [MESSAGE_COMMANDS.MESSAGE_ACK_CHANGED]: { Args: WhatsappChatMessage; Result: Ack };
  [MESSAGE_COMMANDS.MESSAGE_REMOVED]: { Args: WhatsappChatMessage; Result: Ack };
  [MESSAGE_COMMANDS.MESSAGE_REVOKED]: { Args: WhatsappChatMessage; Result: Ack };
  [MESSAGE_COMMANDS.CIPHERTEXT_MESSAGE_ADDED]: { Args: WhatsappChatMessage; Result: Ack };
  [MESSAGE_COMMANDS.RECEIVED_REACTION_ADDED]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.UNREAD_COUNT_CHANGED]: { Args: { chatId: string; unreadCount: number }; Result: Ack };
  [MESSAGE_COMMANDS.CURRENT_CHAT]: { Args: Chat; Result: Ack };
  [MESSAGE_COMMANDS.ME]: { Args: PostMessage; Result: void };
  [MESSAGE_COMMANDS.PROFILE_PIC_THUMB]: { Args: ProfilePicThumbEvent; Result: void };
  [MESSAGE_COMMANDS.INVALID_WA_PHONE_NUMBER]: { Args: { phone?: string }; Result: void };
  [MESSAGE_COMMANDS.GET_POLL_DATA_RESPONSE]: { Args: unknown; Result: void };
  [MESSAGE_COMMANDS.CHECK_IF_REPLYING_TO_MESSAGE_RESPONSE]: { Args: unknown; Result: void };

  // WA webhooks (fire-and-forget; ack only)
  [MESSAGE_COMMANDS.ACK_CHANGED_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.GROUP_ADMIN_CHANGED_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.GROUP_DESCRIPTION_CHANGED_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.GROUP_MESSAGE_PINNED_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.GROUP_NAME_CHANGED_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.MESSAGE_DELETED_REVOKED_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.MESSAGE_EDITED_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.PARTICIPANT_ADDED_BY_ADMIN_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.PARTICIPANT_JOINED_VIA_LINK_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.PARTICIPANT_KICKED_FROM_GROUP_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.PARTICIPANT_LEFT_GROUP_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.POLL_VOTE_WEBHOOK]: { Args: unknown; Result: Ack };
  [MESSAGE_COMMANDS.REPLY_TO_MY_MESSAGE_WEBHOOK]: { Args: unknown; Result: Ack };

  // Queries forwarded to the page world
  [MESSAGE_COMMANDS.CHECK_IF_CHAT_HAS_NEWER_MESSAGES]: { Args: { chatId: string; messageListToCheck: unknown }; Result: unknown };
  [MESSAGE_COMMANDS.CHECK_IF_REPLYING_TO_MESSAGE]: { Args: unknown; Result: unknown };
  [MESSAGE_COMMANDS.CLEAR_CHAT_INPUT]: { Args: string; Result: void };
  [MESSAGE_COMMANDS.CLOSE_MEDIA_PREVIEW]: { Args: unknown; Result: boolean };
  [MESSAGE_COMMANDS.FOCUS_CONTACT]: { Args: string; Result: void };
  [MESSAGE_COMMANDS.GET_CHATS_AND_CONTACTS]: { Args: unknown; Result: { chatList: unknown[]; contactList: unknown[] } };
  [MESSAGE_COMMANDS.GET_MEDIA]: { Args: { chatId: string }; Result: { cmd: "mediaBlobs"; mediaBlobList: unknown[]; caption: string } };
  [MESSAGE_COMMANDS.GET_WA_MESSAGE_ACK_DATA]: { Args: unknown; Result: WaMessageAck[] };
  [MESSAGE_COMMANDS.GET_WA_CONTACT_INFO_FROM_LIST_ITEM_SELECTOR]: { Args: { selector: string }; Result: unknown };
  [MESSAGE_COMMANDS.GET_WA_ME_USER]: { Args: unknown; Result: string };
  [MESSAGE_COMMANDS.GET_WA_VERSION]: { Args: unknown; Result: string };
}

/** Typed payload (Args) for a given command, e.g. MessageCommandArgs<'set-token'>. */
export type MessageCommandArgs<C extends MessageCommand> = C extends keyof MessageCommandContractMap
  ? MessageCommandContractMap[C]["Args"]
  : unknown;

/** Typed response (Result) for a given command. */
export type MessageCommandResult<C extends MessageCommand> = C extends keyof MessageCommandContractMap
  ? MessageCommandContractMap[C]["Result"]
  : unknown;
